/**
 * Achievement unlock toast overlay.
 *
 * Drains `AchievementSystem.pendingToasts` and animates one slide-in card at
 * a time in the top-right corner. Queue is global, so toasts survive state
 * transitions — attach the overlay to both the labyrinth and base states so
 * that an unlock during gameplay is still visible after returning to base.
 */

import { Container, Graphics, Text } from 'pixi.js';
import { drawBevelPanel, drawHeaderBand } from './PanelUtils';
import { ACCENT } from './LayoutConstants';
import type { AchievementSystem, ToastSpec } from '../achievements/AchievementSystem';

const TOAST_W = 320;
const TOAST_H = 72;
const DURATION = 4.0;    // seconds on-screen
const SLIDE_IN = 0.3;
const SLIDE_OUT = 0.4;
const MARGIN = 16;

export interface AchievementToastOverlay {
  container: Container;
  update: (dt: number) => void;
  relayout: (screenW: number) => void;
}

export function buildAchievementToastOverlay(
  achievements: AchievementSystem,
  screenW: number,
): AchievementToastOverlay {
  const container = new Container();
  container.eventMode = 'none';   // don't block input
  let currentToast: {
    spec: ToastSpec;
    container: Container;
    age: number;
  } | null = null;
  let width = screenW;

  function dequeueNext(): ToastSpec | null {
    const q = achievements.pendingToasts;
    if (q.length === 0) return null;
    return q.shift() ?? null;
  }

  function buildCard(spec: ToastSpec): Container {
    const card = new Container();

    const bg = new Graphics();
    drawBevelPanel(bg, 0, 0, TOAST_W, TOAST_H, {
      fill: 0x10141e,
      border: 0x2a3448,
      radius: 6,
      depth: 3,
      accentEdge: 0xffcc44,
    });
    card.addChild(bg);

    const band = new Graphics();
    drawHeaderBand(band, 6, 4, TOAST_W - 12, 0xffcc44, 0x10141e, 18);
    card.addChild(band);

    const tierLabel = spec.tierName ? ` · ${spec.tierName}` : '';
    const title = new Text({
      text: `ACHIEVEMENT UNLOCKED${tierLabel}`,
      style: { fontFamily: 'Offside', fontSize: 10, fill: 0x10141e, fontWeight: '700' },
    });
    title.position.set(12, 7);
    card.addChild(title);

    const name = new Text({
      text: spec.name,
      style: { fontFamily: 'Offside', fontSize: 15, fill: ACCENT, fontWeight: '700' },
    });
    name.position.set(12, 28);
    card.addChild(name);

    const desc = new Text({
      text: spec.description,
      style: { fontFamily: 'Offside', fontSize: 11, fill: 0xaabbcc, wordWrap: true, wordWrapWidth: TOAST_W - 24 },
    });
    desc.position.set(12, 48);
    card.addChild(desc);

    return card;
  }

  function update(dt: number) {
    if (!currentToast) {
      const next = dequeueNext();
      if (!next) return;
      const card = buildCard(next);
      container.addChild(card);
      currentToast = { spec: next, container: card, age: 0 };
    }
    currentToast.age += dt;
    const age = currentToast.age;
    const card = currentToast.container;

    // Slide in from the right, then hold, then slide out.
    const destX = width - TOAST_W - MARGIN;
    if (age < SLIDE_IN) {
      const t = age / SLIDE_IN;
      card.x = destX + (1 - t) * (TOAST_W + MARGIN);
      card.alpha = t;
    } else if (age > DURATION - SLIDE_OUT) {
      const t = Math.min(1, (age - (DURATION - SLIDE_OUT)) / SLIDE_OUT);
      card.x = destX + t * (TOAST_W + MARGIN);
      card.alpha = 1 - t;
    } else {
      card.x = destX;
      card.alpha = 1;
    }
    card.y = MARGIN;

    if (age >= DURATION) {
      container.removeChild(card);
      card.destroy({ children: true });
      currentToast = null;
    }
  }

  function relayout(newScreenW: number) {
    width = newScreenW;
  }

  return { container, update, relayout };
}
